import type { DAGNode, DAGEdge, LayoutNode } from './types';

/**
 * Performs topological sort and layer assignment for DAG layout.
 * Uses Kahn's algorithm to assign nodes to layers based on dependency depth.
 * Distributes nodes within each layer evenly across the horizontal space.
 */
export function layoutDAG(
  nodes: DAGNode[],
  edges: DAGEdge[],
  width: number,
  height: number,
): LayoutNode[] {
  const padding = 60;
  const availableWidth = width - padding * 2;
  const availableHeight = height - padding * 2;

  // Build adjacency lists and in-degree map
  const adjacencyList = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  const nodeMap = new Map<string, DAGNode>();

  // Initialize structures
  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
    adjacencyList.set(node.id, []);
    inDegree.set(node.id, 0);
  });

  // Build graph
  edges.forEach((edge) => {
    const fromList = adjacencyList.get(edge.from);
    if (fromList) {
      fromList.push(edge.to);
    }
    inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
  });

  // Topological sort with layer assignment
  const layers: string[][] = [];
  const queue: string[] = [];
  const nodeLayer = new Map<string, number>();

  // Start with nodes that have no dependencies
  nodes.forEach((node) => {
    if (inDegree.get(node.id) === 0) {
      queue.push(node.id);
      nodeLayer.set(node.id, 0);
    }
  });

  // Process nodes layer by layer
  while (queue.length > 0) {
    const currentLayerSize = queue.length;
    const currentLayer: string[] = [];

    for (let i = 0; i < currentLayerSize; i++) {
      const nodeId = queue.shift()!;
      currentLayer.push(nodeId);

      const layer = nodeLayer.get(nodeId) ?? 0;
      const neighbors = adjacencyList.get(nodeId) ?? [];

      neighbors.forEach((neighborId) => {
        const currentInDegree = inDegree.get(neighborId) ?? 0;
        inDegree.set(neighborId, currentInDegree - 1);

        if (currentInDegree - 1 === 0) {
          queue.push(neighborId);
          nodeLayer.set(neighborId, layer + 1);
        }
      });
    }

    layers.push(currentLayer);
  }

  // Calculate positions
  const layoutNodes: LayoutNode[] = [];
  const numLayers = layers.length;
  const layerHeight = numLayers > 1 ? availableHeight / (numLayers - 1) : 0;

  layers.forEach((layer, layerIndex) => {
    const numNodesInLayer = layer.length;
    const layerWidth =
      numNodesInLayer > 1 ? availableWidth / (numNodesInLayer - 1) : availableWidth / 2;

    layer.forEach((nodeId, indexInLayer) => {
      const node = nodeMap.get(nodeId);
      if (!node) return;

      const x =
        numNodesInLayer === 1 ? padding + availableWidth / 2 : padding + indexInLayer * layerWidth;

      const y =
        numLayers === 1 ? padding + availableHeight / 2 : padding + layerIndex * layerHeight;

      layoutNodes.push({
        ...node,
        x,
        y,
        layer: layerIndex,
      });
    });
  });

  return layoutNodes;
}
